import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Grid,
  Modal,
  Stack,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { toBlob } from 'html-to-image';
import React, { useEffect, useState } from 'react';
import CloseModalIcon from 'src/content/common/Common';
import { UserFormData, UserInfos } from 'src/types';
import { updatePasswordAsync } from 'src/utils/api/auth.api';
import { generateMatricule, useCreateAdmin } from 'src/utils/api/hooks';
import { COLORS, modalStyles } from 'src/utils/constants';
import { useFormData } from 'src/utils/form';
import Swal from 'sweetalert2';
import {
  createCotisationsync,
  toggleUserStatus,
  updateUserCardMemberAsync
} from '../../../utils/api/user.api';
import { SwitchButton } from '../../common/Table';
import { DashboardUser } from '../dashboard/DashboardAdmin';

type addUserModalProps = {
  show: boolean;
  onHide: () => any;
  item?: UserInfos;
  admin: boolean;
};

export function AddUserModal({ show, onHide, item, admin }: addUserModalProps) {
  const [img, setImg] = useState<File>();
  const { loading, onAddAdmin } = useCreateAdmin(
    () => {
      onHide();
    },
    item,
    img
  );
  const { register, onSubmitForm, errors } = useFormData<UserFormData>(
    {
      email: item?.email || '',
      firstName: item?.firstName || '',
      matricule: item?.matricule || '',
      domicile: item?.domicile || '',
      lastName: item?.lastName || '',
      phone: item?.phone || ''
    },
    (yup) => ({
      email: yup.string().required().label('Email').email(),
      firstName: yup.string().required().label('Prénom'),
      lastName: yup.string().required().label('Nom'),
      phone: yup.string().required().label('Téléphone')
    })
  );

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon
          title={
            item
              ? 'Modifier'
              : `Ajouter ${admin ? 'un administrateur' : 'un utilisateur'}`
          }
          {...{ onHide }}
        />
        <form
          onSubmit={onSubmitForm((data) => {
            const newData = { ...data };
            if (!item) {
              newData.isAdmin = admin;
            }
            onAddAdmin(newData);
          })}
        >
          <Grid container spacing={{ md: 2 }}>
            <Grid item sm={12} md={6}>
              <TextField
                type="text"
                {...register('firstName')}
                margin="normal"
                required
                fullWidth
                placeholder={'Prénom'}
                label={'Prénom'}
                variant="outlined"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                type="text"
                {...register('lastName')}
                margin="normal"
                required
                fullWidth
                placeholder={'Nom'}
                label={'Nom'}
                variant="outlined"
              />
            </Grid>

            <Grid item sm={12} md={6}>
              <TextField
                type="phone"
                {...register('phone')}
                margin="normal"
                required
                fullWidth
                placeholder={'Téléphone'}
                label={'Téléphone'}
                variant="outlined"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                fullWidth
                type="email"
                {...register('email')}
                margin="normal"
                required
                disabled={!!item}
                placeholder={'Email'}
                label={'Email'}
                variant="outlined"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                type="text"
                {...register('matricule')}
                margin="normal"
                fullWidth
                placeholder={'Matricule'}
                label={'Matricule'}
                variant="outlined"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                type="text"
                {...register('domicile')}
                margin="normal"
                fullWidth
                placeholder={'Domicile'}
                label={'Domicile'}
                variant="outlined"
              />
            </Grid>
            {!!item && (
              <Grid item sm={12} md={6}>
                <Typography variant="body2">Photo de profil</Typography>
                <TextField
                  type="file"
                  margin="normal"
                  fullWidth
                  onChange={(e) => {
                    if ('files' in e.target) {
                      setImg(e.target.files[0]);
                    }
                  }}
                  inputProps={{ accept: 'image/*' }}
                  variant="outlined"
                />
                {!!img && (
                  <Box
                    component={'img'}
                    src={URL.createObjectURL(img)}
                    sx={{ width: '100%', height: 'auto' }}
                  />
                )}
              </Grid>
            )}
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              fullWidth
              onClick={onHide}
              variant="contained"
              color="error"
            >
              Annuler
            </Button>
            <LoadingButton
              loading={loading}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {item ? 'Modifier' : 'Ajouter'}
            </LoadingButton>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

export function UpdateUserModal({
  show,
  onHide,
  item
}: addUserModalProps & { item: UserInfos }) {
  return (
    <AddUserModal
      admin={item.isAdmin}
      onHide={onHide}
      show={show}
      item={item}
    />
  );
}

type UpdatePasswordFormData = { password: string; password_confirm: string };
export function UpdateUserPassword({ show, onHide }: addUserModalProps) {
  const [loading, setLoading] = useState(false);
  const { register, setErrors, onSubmitForm } =
    useFormData<UpdatePasswordFormData>(
      {
        password: '',
        password_confirm: ''
      },
      (Yup) => ({
        password: Yup.string().required().label('Mot de passe').min(5),
        password_confirm: Yup.string().oneOf(
          [Yup.ref('password'), null],
          'Les Mot de passe ne correspondent pas'
        )
      })
    );

  const onSubmit = React.useCallback(async (data: UpdatePasswordFormData) => {
    setLoading(true);
    try {
      await updatePasswordAsync(data.password);
      onHide();
      Swal.fire({
        icon: 'success',
        iconColor: COLORS.primary,
        confirmButtonColor: COLORS.primary,
        title: 'Mot de passe modifiés avec succès'
      });
    } catch (ex: any) {
      if (ex.code === 'auth/requires-recent-login') {
        setErrors({
          password: 'Il faut te déconnecter et reconnecter pour continuer'
        });
      } else {
        setErrors({ password: 'Une erreur est survenue' });
      }
    }
    setLoading(false);
  }, []);

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon title={'Mot de passe'} {...{ onHide }} />
        <form onSubmit={onSubmitForm(onSubmit)}>
          <TextField
            type="password"
            {...register('password')}
            margin="normal"
            required
            fullWidth
            placeholder={'Nouveau mot de passse'}
            label={'Nouveau mot de passse'}
            variant="outlined"
          />

          <TextField
            type="password"
            {...register('password_confirm')}
            margin="normal"
            required
            fullWidth
            placeholder={'Confirmer mot de passe'}
            label={'Confirmer mot de passe'}
            variant="outlined"
          />

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              onClick={onHide}
              variant="contained"
              color="error"
            >
              Annuler
            </Button>
            <LoadingButton
              loading={loading}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {'Modifier'}
            </LoadingButton>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

type UpdateCardMemberProps = {
  show: boolean;
  onHide: () => void;
  item: UserInfos;
};

export function UpdateCardMemberModal({
  show,
  onHide,
  item
}: UpdateCardMemberProps) {
  const [loading, setLoading] = useState(false);
  const [loading1, setLoading1] = useState(false);
  const [matricule, setMatricule] = useState('');
  const [img, setImg] = useState(
    'https://ui-avatars.com/api/?name=KS&size=210&background=0F7C55&color=FFF'
  );

  useEffect(() => {
    if (item.avatar) {
      setImg(item.avatar);
    }
    if (item.matricule) setMatricule(item.matricule);
    else generateMatricule().then(setMatricule);
  }, [item]);

  const onSubmit = React.useCallback(
    async (cotisation: boolean) => {
      const setLoadingFunc = cotisation ? setLoading1 : setLoading;
      setLoadingFunc(true);
      try {
        const blob = await toBlob(document.getElementById(item.uid), {
          type: 'image/png'
        });
        if (blob) {
          const update: UserFormData = {};
          if (item.matricule !== matricule) {
            update.matricule = matricule;
          }
          await updateUserCardMemberAsync(item.uid, blob, update);
          Swal.fire({
            icon: 'success',
            iconColor: COLORS.primary,
            confirmButtonColor: COLORS.primary,
            title: 'Carte membre modifiés avec succès'
          });
        }
        if (cotisation) {
          await createCotisationsync({
            amount: 1000,
            name: `${item.firstName} ${item.lastName}`,
            phone: item.phone,
            type: 'inscription',
            user: item.uid
          });
        }
        onHide();
      } catch (ex: any) {
        console.log('error', ex);
        Swal.fire({
          icon: 'error',
          iconColor: COLORS.red,
          confirmButtonColor: COLORS.red,
          title: 'Une erreur est survenue'
        });
      }
      setLoadingFunc(false);
    },
    [matricule]
  );

  const disabled = !matricule || loading || loading1;

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon
          title={`Carte membre ${item.firstName}`}
          {...{ onHide }}
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Stack direction={'row'} alignItems={'flex-end'}>
            <div id={item.uid} style={{ width: 600, position: 'relative' }}>
              <img src={'/modele.png'} style={{ width: 600, height: 'auto' }} />
              <div
                style={{
                  position: 'absolute',
                  top: 125,
                  bottom: 0,
                  left: 45,
                  right: 0,
                  flexDirection: 'row',
                  display: 'flex'
                }}
              >
                <img
                  src={img}
                  style={{
                    width: 210,
                    height: 210,
                    objectFit: 'cover',
                    borderRadius: 10,
                    overflow: 'hidden'
                  }}
                />
                <div style={{ paddingLeft: 45, paddingTop: 5 }}>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      lineHeight: '80px',
                      display: 'block'
                    }}
                  >
                    {item.firstName} {item.lastName}
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      display: 'block'
                    }}
                  >
                    {item.phone}
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      lineHeight: '75px',
                      display: 'block'
                    }}
                  >
                    {item.domicile || 'N.A'}
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      display: 'block'
                    }}
                  >
                    {matricule}
                  </span>
                </div>
              </div>
            </div>
            <Stack direction="column" spacing={2} sx={{ ml: 2, flexGrow: 1 }}>
              {!!item.cardMember && (
                <Button
                  fullWidth
                  size="large"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(item.cardMember, '_blank');
                  }}
                  variant={'outlined'}
                >
                  {'Imprimer'}
                </Button>
              )}
              <LoadingButton
                loading={loading}
                fullWidth
                size="large"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSubmit(false);
                }}
                disabled={disabled}
                variant={!item.matricule ? 'outlined' : 'contained'}
              >
                {item.cardMember ? 'Regénérer' : 'Générer'}
              </LoadingButton>
              {!item.matricule && (
                <LoadingButton
                  loading={loading1}
                  fullWidth
                  size="large"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSubmit(true);
                  }}
                  disabled={disabled}
                  variant="contained"
                >
                  {'Générer + Inscription'}
                </LoadingButton>
              )}
              <Button
                fullWidth
                onClick={onHide}
                size="large"
                variant="contained"
                color="error"
              >
                Annuler
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

type ShowUserModalProps = {
  show: boolean;
  onHide: () => void;
  item: UserInfos;
};

export function ShowUserModal({ show, onHide, item }: ShowUserModalProps) {
  const DATA = [
    { title: 'Prénom', value: item.firstName },
    { title: 'Nom', value: item.lastName },
    { title: 'Email', value: item.email },
    { title: 'Téléphone', value: item.phone },
    { title: 'Matricule', value: item.matricule || 'N.A' },
    { title: 'Domicile', value: item.domicile || 'N.A' },
    {
      title: 'Bloqué?',
      value: (
        <SwitchButton
          updateFunction={(value) => toggleUserStatus(item.uid)}
          value={!!item.disabled}
        />
      )
    },
    { title: 'Carte membre', value: <UpdateMemberCard item={item} /> }
  ];

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon {...{ onHide }} />
        <Grid container spacing={2}>
          <Grid item md={4} sm={12}>
            <Avatar
              sx={{ width: '100%', height: 'auto' }}
              variant="rounded"
              alt={item.firstName}
              src={item.avatar}
            />
          </Grid>
          <Grid item md={8} sm={12}>
            <Table>
              {DATA.map((it) => (
                <TableRow key={it.title}>
                  <TableCell align={'center'} padding={'normal'}>
                    {it.title}
                  </TableCell>
                  <TableCell align={'center'} padding={'normal'}>
                    {it.value}
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </Grid>
          <Grid item md={12} sm={12}>
            <DashboardUser item={item} />
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

export function UpdateMemberCard({ item }: { item: UserInfos }) {
  const [show, setShow] = useState(false);
  return (
    <Stack spacing={1} direction={'column'} alignItems={'center'}>
      {show && (
        <UpdateCardMemberModal
          show={show}
          onHide={() => setShow(false)}
          item={item}
        />
      )}
      {!!item.cardMember && (
        <Box
          component={'img'}
          src={item.cardMember}
          onClick={() => setShow(true)}
          sx={{ width: 80, height: 'auto', cursor: 'pointer' }}
        />
      )}
      {!item.cardMember && (
        <Button
          onClick={() => setShow(true)}
          size="small"
          type="submit"
          variant="contained"
        >
          Générer
        </Button>
      )}
    </Stack>
  );
}
